/**
 * Global Moment Mixin
 * Provides globally accessible moment.js date formatting methods
 * @mixin GlobalMoment
 * @requires moment
 *
 * This mixin has a major issue with time zone context
 *   - Moment.js will always assume the browser's local time zone unless explicitly told otherwise.
 *   - Many, but not all, places in the app expect dates to be shown in the user's preferred time zone (set in their profile)
 *   - This mixin does not handle that, leading to potential discrepancies.
 *
 *  A potential solution for this is to create a new composable (needed for vue 3)
 *   - Include these methods or similar
 *   - Add parameter to specify time zone context (wall clock (browser) vs user preferred)
 *   - Actually, I think the ideal circumstance would be to only use js dates for wall clock context and handle preferred time zone on the server side
 */
export default {
    methods: {
        getUserTimezone() {
            return window.Laravel.user?.timezone ?? moment.tz.guess();
        },
        globalMoment(value) {
            return moment(value).format();
        },
        /*prepareDate(value) {
            return value;
            return moment(new Date(value)).utc(true).format().substring(0, 19);
        },*/
        gMoment(value, format, int, unit) {
            return moment(new Date(value)).add(int, unit).format(format);
        },
        globalMomentDateDots(value) {
            return moment(value).format("M.D.YY");
        },
        globalMomentDate(value, format = "ll") {
            return moment(value).format(format);
        },
        globalMomentDateNoYear(value) {
            return moment(value).format("MMM DD");
        },
        globalMomentLongDate(value) {
            return moment(new Date(value))
                .tz("UTC")
                .format("dddd, MMM DD, YYYY");
        },
        globalMomentLocalLongDate(value) {
            return moment(new Date(value)).format("dddd, MMM DD, YYYY");
        },
        globalMomentUTC(value) {
            return moment(new Date(value)).tz("UTC");
        },
        globalMomentUTCDate(value) {
            return moment(new Date(value)).tz("UTC").format("L");
        },
        globalMomentCompareDate(value) {
            return moment(value).format("L");
        },
        globalMomentYMD(value) {
            return moment(new Date(value)).format("YYYY-MM-DD");
        },
        globalMomentBirthday(value) {
            return moment(value).format("MM/DD/YYYY");
        },
        globalMomentDateShort(value) {
            return moment.utc(value).format("ddd, M/D/YY");
        },
        globalMomentMonth(value) {
            return moment(value).format("MMM");
        },
        globalMomentMonthFull(value) {
            return moment(value).format("MMMM");
        },
        globalMomentMonthYear(value) {
            return moment(value).format("MMMM YYYY");
        },
        globalMomentUTCMonthYear(value) {
            return moment(value).tz("UTC").format("MMMM YYYY");
        },
        globalMomentUTCMonthYearShort(value) {
            return moment(value).format("MMM YYYY");
        },
        globalMomentYear(value) {
            return moment(value).format("YYYY");
        },
        globalMomentDay(value) {
            return moment(value).format("DD");
        },
        globalMomentWeekDay(value) {
            return moment(value).format("dddd");
        },
        globalMomentWeekDayShort(value) {
            return moment(value).format("ddd");
        },
        globalMomentDateTime(value) {
            return moment(value);
        },
        globalMomentDateAtTime(value) {
            let tz = window.Laravel.user.timezone
                ? window.Laravel.user.timezone
                : "America/New_York";
            if (tz !== "America/New_York") {
                return `${moment(value).format("ddd")} at ${moment(value)
                    //.tz(tz)
                    .format("h:mma z")}`;
            } else {
                return `${moment(value).format("ddd")} at ${moment(
                    value
                ).format("h:mma")}`;
            }
        },
        globalMomentFullDateAtTimeShort(value) {
            let tz = window.Laravel.user.timezone
                ? window.Laravel.user.timezone
                : "America/New_York";
            if (tz !== "America/New_York") {
                return `${moment(value).format("ddd, M/D/YY")} at ${moment(
                    value
                )
                    //.tz(tz)
                    .format("h:mma z")}`;
            } else {
                return `${moment(value).format("ddd, M/D/YY")} at ${moment(
                    value
                ).format("h:mma")}`;
            }
        },
        globalMomentFullDateAtTime(value) {
            let tz = window.Laravel.user.timezone
                ? window.Laravel.user.timezone
                : "America/New_York";
            if (tz !== "America/New_York") {
                return `${moment(value).format("dddd, MMM D")} at ${moment(
                    value
                )
                    //.tz(tz)
                    .format("h:mma z")}`;
            } else {
                return `${moment(value).format("dddd, MMM D")} at ${moment(
                    value
                ).format("h:mma")}`;
            }
        },
        globalMomentFullDateAtTimeSeconds(value) {
            let tz = window.Laravel.user.timezone
                ? window.Laravel.user.timezone
                : "America/New_York";
            if (tz !== "America/New_York") {
                return `${moment(value).format("dddd, MMM D")} at ${moment(
                    value
                )
                    //.tz(tz)
                    .format("h:mm:ss a z")}`;
            } else {
                return `${moment(value).format("dddd, MMM D")} at ${moment(
                    value
                ).format("h:mm:ss a")}`;
            }
        },
        globalMomentDateAtTimeDots(value) {
            return `${moment(value).format("M.D.YY")} at ${moment(value).format(
                "h:mma"
            )}`;
        },
        globalMomentTime(value) {
            let tz = window.Laravel.user.timezone
                ? window.Laravel.user.timezone
                : "America/New_York";
            if (tz !== "America/New_York") {
                return `${moment(value).format("h:mma z")}`;
            } else {
                return moment(value).format("h:mma");
            }
        },
        globalMomentTime2(value) {
            let tz = window.Laravel.user.timezone
                ? window.Laravel.user.timezone
                : "America/New_York";
            if (tz !== "America/New_York") {
                return `${moment(value).format("h:mm a z")}`;
            } else {
                return moment(value).format("h:mm a");
            }
        },
        globalMomentISO(value) {
            return moment(value).toISOString().substr(0, 10);
        },
        globalMomentFullISO(value) {
            return moment(value).toISOString();
        },
        globalMoment24Time(value) {
            return moment(value).format("HH:mm");
        },
        globalMomentGetTime(val) {
            return moment(new Date("2000/01/01" + " " + val)).format("HH:mm");
        },
        globalMomentDateMinusWeek(val) {
            return moment(val).subtract(1, "week").toISOString().substr(0, 10);
        },
        globalMomentNextMonth(val, format = "M.D.YY") {
            return moment(val).add(1, "M").startOf("month").format(format);
        },
        globalMomentStartOfMonth(val) {
            return moment(val).startOf("month").format("M.D.YY");
        },
        globalMomentISOStartOfMonth(val) {
            return moment(val).startOf("month").toISOString().substr(0, 10);
        },
        globalMomentNextMonthTitle(val) {
            return moment(val).add(1, "M").startOf("month").format("MMMM");
        },
        globalMoment30Days(val) {
            return moment(val).add(30, "days").format("M.D.YY");
        },
        globalMomentTz(val = moment.tz.guess(), short = false, symbol = false) {
            const abbr = moment.tz(val).format("z");

            let abbrs = {
                EST: "Eastern Standard Time",
                EDT: "Eastern Daylight Time",
                CST: "Central Standard Time",
                CDT: "Central Daylight Time",
                MST: "Mountain Standard Time",
                MDT: "Mountain Daylight Time",
                PST: "Pacific Standard Time",
                PDT: "Pacific Daylight Time",
            };

            if (symbol) {
                return `${abbr} ${moment.tz(val).utcOffset() / 60}`;
            }

            if (short) {
                abbrs = {
                    EST: "Eastern Time",
                    EDT: "Eastern Time",
                    CST: "Central Time",
                    CDT: "Central Time",
                    MST: "Mountain Time",
                    MDT: "Mountain Time",
                    PST: "Pacific Time",
                    PDT: "Pacific Time",
                };
            }

            return abbrs[abbr] ?? val;
        },
        globalMomentAddWeek(value) {
            return moment(value).add(1, "W");
        },
        globalMomentEndOfMonth(val) {
            return moment(val).endOf("month").format("YYYY-MM-DD");
        },
        globalMomentEndOfNextMonth(val) {
            return moment(val)
                .add(1, "month")
                .endOf("month")
                .format("YYYY-MM-DD");
        },
        globalMomentDateDotsUTC(value) {
            return moment(value).tz("UTC").format("M.D.YY");
        },
    },
};
